import React, { useEffect, useState } from 'react';

import clsx from 'clsx';
import { Helmet } from 'react-helmet';
import { Outlet, useLocation } from 'react-router-dom';
import { CSSTransition } from 'react-transition-group';

import { LoadingType, setPreQualificationStep } from 'actions';
import { InfoIcon, LogoImage } from 'assets/images';
import { HFS_PRIVACY_POLICY_URL, HFS_TERMS_OF_USE_URL } from 'constants/constants';
import SignOut from 'components/SignOut';
import Tooltip from 'components/Tooltip';
import { DashboardStep } from 'enums/DashboardStep';
import { PreQualificationStep } from 'enums/PreQualificationStep';
import { useAppDispatch } from 'hooks/useAppDispatch';
import useAppNavigate from 'hooks/useAppNavigate';
import { useAppSelector } from 'hooks/useAppSelector';
import { AppRoutes } from 'routes/routes.enums';
import SignInButton from 'pages/Main/SignInButton';

import styles from './Main.module.scss';

const ADDITIONAL_FOOTER_TEXT = (
  <>
    The above is based on information that you submitted and assumes the accuracy of such information. This does not
    constitute an actual commitment to lend or an offer to extend credit. A commitment to lend can only be issued upon
    satisfactory submission of (1) a complete application, supporting documents, (2) verification of your income,
    assets, and financial condition, (3) approval of the requested home improvement project/contractor, (4) no adverse
    change in your credit profile, and (5) satisfaction of any other conditions and/or requirements specified in any
    separate tentative approval communication. Please note that actual interest rate and terms cannot be set until a
    complete application has been submitted and all rates/terms are subject to change without prior notice. The fee that
    HFS will charge when you obtain a loan through the HFS platform is calculated based on a percentage of your funded
    loan and is described in your Credit Services Agreement and will be detailed in loan documents provided by a
    participating lender. APR means Annual Percentage Rate. Loan Example: A closed-end loan from a third-party lender
    for $60,000 with a fixed rate of 7.99%, a one-time fee of $2,394, and an APR of 8.52% for a term of 20 years would
    result in 240 payments of $521,50, assuming the loan is not paid off early. Loan programs described at this website
    have no prepayment penalties.  Not all will qualify. HFS is not liable for typographical or data transmission
    errors. Florida loans may be subject to a Document Stamp Tax Fee. This fee is not quoted in the above APR's. All
    information is subject to change without notice.
  </>
);
const ADDITIONAL_FOOTER_TOOLTIP_TEXT =
  '*Florida loans may be subject to a Document Stamp Tax. This fee is not quoted in the above APR.';
const TOOLTIP_TEXT = `Florida Document Stamp Tax \n
The state of Florida's Department of Revenue requires a tax on loans funded. 
This amounts to approximately $3.50 on every $1,000). 
This tax will be added to your gross loan amount should your loan fund.`;
const HFS_URL = 'https://www.hfsfinancial.net/';

const Main = () => {
  const dispatch = useAppDispatch();
  const navigator = useAppNavigate();
  const { currentPreQualificationStep, currentDashboardStep } = useAppSelector((state) => state.stages);
  const [isDashboard, setIsDashboard] = useState(false);
  const [isPrequalification, setIsPrequalification] = useState(false);
  const loading = useAppSelector((state) => state.loading[LoadingType.GetData]);
  const dashboardPageLoading = useAppSelector((state) => state.loading[LoadingType.DashboardPageUpdating]);
  const { firstName, lastName } = useAppSelector((state) => state.borrower);
  const { pathname } = useLocation();

  const isLogoutShow = () => {
    const accessToken = localStorage.getItem('token');

    return (
      (firstName || lastName) &&
      accessToken &&
      (isDashboard ||
        (isPrequalification &&
          (currentPreQualificationStep === null ||
            currentPreQualificationStep === PreQualificationStep.BorrowerIdentity ||
            currentPreQualificationStep === PreQualificationStep.BorrowerInfo ||
            currentPreQualificationStep === PreQualificationStep.LoanInformation)))
    );
  };

  const isNeedToShowAdditionalFooter =
    isDashboard &&
    !loading &&
    !dashboardPageLoading &&
    [
      DashboardStep.AcceptManualOneTermOffer,
      DashboardStep.AcceptManualMultiTermOffer,
      DashboardStep.AcceptPosAutomatedOffer,
      DashboardStep.AcceptHilAutomatedOffer,
    ].includes(currentDashboardStep!);

  useEffect(() => {
    setIsDashboard(pathname === AppRoutes.Dashboard);
    setIsPrequalification(pathname === AppRoutes.Prequalification);
  }, [pathname]);

  const onOpenApplicationFlow = () => {
    dispatch(setPreQualificationStep(PreQualificationStep.BorrowerIdentity));
    navigator(AppRoutes.Prequalification);
  };

  return (
    <>
      <div id="main" className={styles.mainContainer}>
        <Helmet>
          <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1" />
        </Helmet>
        <div className={clsx(styles.header, isDashboard && styles.dashboardHeader)}>
          <div className={styles.imageContainer}>
            <LogoImage className={styles.image} onClick={onOpenApplicationFlow} />
          </div>
          {isLogoutShow() && <SignOut href={isDashboard ? HFS_URL : undefined} className={styles.signOut} />}
          {!isLogoutShow() &&
            isPrequalification &&
            (currentPreQualificationStep === null ||
              currentPreQualificationStep === PreQualificationStep.LoanInformation ||
              currentPreQualificationStep === PreQualificationStep.BorrowerIdentity ||
              currentPreQualificationStep === PreQualificationStep.BorrowerInfo) && <SignInButton />}
        </div>
        <div
          className={clsx(styles.main, isDashboard && styles.dashboardMain, {
            [styles.mainLoginPage]: pathname === AppRoutes.Login,
          })}
        >
          <Outlet />
        </div>
        {isNeedToShowAdditionalFooter && (
          <CSSTransition
            in={isNeedToShowAdditionalFooter}
            timeout={2000}
            classNames={{
              enter: styles.enter,
              enterActive: styles.enterActive,
              exit: styles.exit,
              exitActive: styles.exitActive,
            }}
          >
            <div className={styles.additionalFooter}>
              <div className={styles.tooltipContainer}>
                {ADDITIONAL_FOOTER_TOOLTIP_TEXT}
                <Tooltip title={TOOLTIP_TEXT} children={<InfoIcon className={styles.iconInfo} />} />
              </div>
              <p className={styles.additionalFooterText}>{ADDITIONAL_FOOTER_TEXT}</p>
            </div>
          </CSSTransition>
        )}
        <div id="mainFooter" className={styles.footer}>
          <p>
            Copyright © {new Date().getFullYear()} HFS Financial. All rights reserved |{' '}
            <a className={styles.link} href={HFS_PRIVACY_POLICY_URL} target="_blank" rel="noopener noreferrer">
              Privacy Policy
            </a>{' '}
            |{' '}
            <a className={styles.link} href={HFS_TERMS_OF_USE_URL} target="_blank" rel="noopener noreferrer">
              Terms of Use
            </a>
          </p>
          <p>NMLS ID # 1680766</p>
        </div>
      </div>
    </>
  );
};

export default Main;
